// src/pages/sqf/SqfFillPage.js
// Mobile-first checklist fill page
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';
import sqfService from '../../services/sqfService';
import { compressImage } from '../../utils/imageCompress';
import { useLanguage, t } from '../../context/LanguageContext';

const STATUS_BADGE = { pass: 'success', flag: 'danger', uncertain: 'secondary' };

// Formats a valueText for display — unwraps multi-reading JSON arrays into "v1, v2, v3", or an
// item_count_list's rows into "Item: count (was previous)" (deviating rows only, if any).
function formatValue(valueText) {
  if (!valueText) return 'empty';
  try {
    const parsed = JSON.parse(valueText);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed) && Array.isArray(parsed.criteria)) {
      const flagged = parsed.criteria.filter(c => c.aiStatus === 'flag' || c.withinTolerance === false);
      const list = flagged.length > 0 ? flagged : parsed.criteria;
      return list.map(c => (c.key === 'weight' && c.diffPercent != null)
        ? `${c.label}: ${c.actualValue}${c.targetUnit || ''} (${c.diffPercent}% ${c.withinTolerance ? 'within tolerance' : 'off target'})`
        : `${c.label}: ${c.aiStatus}`
      ).join(', ') || 'empty';
    }
    if (Array.isArray(parsed)) {
      if (parsed.length > 0 && parsed[0] && typeof parsed[0] === 'object' && 'itemName' in parsed[0]) {
        const deviating = parsed.filter(r => r.isDeviation);
        const list = deviating.length > 0 ? deviating : parsed;
        return list.map(r => `${r.itemName}: ${r.count ?? '—'}${r.previousCount != null ? ` (was ${r.previousCount})` : ''}`).join(', ') || 'empty';
      }
      const vals = parsed
        .map(r => (typeof r === 'object' && r !== null ? r.value : r))
        .filter(v => v !== '' && v !== null && v !== undefined);
      return vals.length ? vals.join(', ') : 'empty';
    }
  } catch { /* not JSON */ }
  return valueText;
}

// Live, client-side mirror of sqfValidation.js's item_count_list deviation logic — for instant
// UX feedback only; the server always recomputes authoritatively from real prior-submission data
// at submit time, never trusting this.
function computeIsDeviation(mode, threshold, previousCount, count) {
  if (previousCount == null || count === '' || count == null || isNaN(Number(count))) return false;
  const c = Number(count);
  if (mode === 'threshold') return Math.abs(c - previousCount) > (threshold || 0);
  if (mode === 'decrease_only') return c < previousCount;
  return c !== previousCount; // any_change (default)
}

function getCurrentUser() {
  try {
    const token = localStorage.getItem('token');
    if (!token) return { id: null, name: 'Unknown' };
    const d = jwtDecode(token);
    return { id: d.id, name: d.username || 'Unknown' };
  } catch { return { id: null, name: 'Unknown' }; }
}

// ─── Field renderers ──────────────────────────────────────────────────────────

function NumberField({ field, value, onChange, disabled, responses, allFields }) {
  const v = field.validation || {};
  const n = parseFloat(value);

  // Resolve effective range — prefer linked field's per-option range over static min/max
  let effectiveMin = v.min;
  let effectiveMax = v.max;
  let rangeHint = null;

  if (field.linkedFieldId && responses && allFields) {
    const linkedField = allFields.find(f => f.id === field.linkedFieldId);
    const linkedValue = responses[field.linkedFieldId];
    if (linkedField && linkedValue && linkedField.optionRanges?.[linkedValue]) {
      const range = linkedField.optionRanges[linkedValue];
      if (range.min !== undefined) effectiveMin = range.min;
      if (range.max !== undefined) effectiveMax = range.max;
      rangeHint = `Recommended for "${linkedValue}": ${range.min ?? '?'} – ${range.max ?? '?'}${field.unit ? ' ' + field.unit : ''}`;
    } else if (linkedField && !linkedValue) {
      rangeHint = `Select "${linkedField.label}" above to see recommended range`;
    }
  }

  const outOfRange = !isNaN(n) && (
    (effectiveMin !== undefined && v.deviationIfBelow && n < effectiveMin) ||
    (effectiveMax !== undefined && v.deviationIfAbove && n > effectiveMax)
  );

  return (
    <div>
      <div className="input-group">
        <input
          type="number"
          className={`form-control ${outOfRange ? 'border-danger' : ''}`}
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          placeholder={effectiveMin !== undefined && effectiveMax !== undefined ? `${effectiveMin} – ${effectiveMax}` : ''}
          disabled={disabled}
        />
        {field.unit && <span className="input-group-text">{field.unit}</span>}
      </div>
      {rangeHint && !outOfRange && (
        <div className="text-muted mt-1" style={{ fontSize: 11 }}>ℹ {rangeHint}</div>
      )}
      {outOfRange && (
        <div className="text-danger mt-1" style={{ fontSize: 12 }}>
          ⚠ Out of range ({effectiveMin !== undefined ? `min ${effectiveMin}` : ''}{effectiveMin !== undefined && effectiveMax !== undefined ? ', ' : ''}{effectiveMax !== undefined ? `max ${effectiveMax}` : ''})
        </div>
      )}
      {field.helpText && <div className="text-muted mt-1" style={{ fontSize: 11 }}>{field.helpText}</div>}
    </div>
  );
}

// Multi-reading number field — stores values as JSON array in valueText
function MultiReadingNumberField({ field, value, onChange, disabled, responses, allFields }) {
  const minCount = field.minReadings ?? 1;
  const v = field.validation || {};

  // Parse current readings — each entry is {value, at, by} or legacy string
  const readings = (() => {
    try {
      const parsed = JSON.parse(value || '[]');
      if (Array.isArray(parsed)) {
        // Normalise: legacy string entries → {value, at: null, by: null}
        const normalised = parsed.map(r =>
          typeof r === 'object' && r !== null ? r : { value: String(r ?? ''), at: null, by: null }
        );
        while (normalised.length < minCount) normalised.push({ value: '', at: null, by: null });
        return normalised;
      }
    } catch { /* fall through */ }
    const arr = [];
    while (arr.length < minCount) arr.push({ value: '', at: null, by: null });
    return arr;
  })();

  // Resolve effective range from linked field
  let effectiveMin = v.min;
  let effectiveMax = v.max;
  let rangeHint = null;
  if (field.linkedFieldId && responses && allFields) {
    const linkedField = allFields.find(f => f.id === field.linkedFieldId);
    const linkedValue = responses[field.linkedFieldId];
    if (linkedField && linkedValue && linkedField.optionRanges?.[linkedValue]) {
      const range = linkedField.optionRanges[linkedValue];
      if (range.min !== undefined) effectiveMin = range.min;
      if (range.max !== undefined) effectiveMax = range.max;
      rangeHint = `Recommended for "${linkedValue}": ${range.min ?? '?'} – ${range.max ?? '?'}${field.unit ? ' ' + field.unit : ''}`;
    }
  }

  const serialize = (arr) => onChange(JSON.stringify(arr));

  const updateRow = (idx, val) => {
    const { id: userId, name: userName } = getCurrentUser();
    const next = [...readings];
    next[idx] = { value: val, at: new Date().toISOString(), by: userId, byName: userName };
    serialize(next);
  };

  const addRow = () => serialize([...readings, { value: '', at: null, by: null, byName: null }]);

  const removeRow = (idx) => {
    if (readings.length <= minCount) return;
    const next = readings.filter((_, i) => i !== idx);
    serialize(next);
  };

  return (
    <div>
      {rangeHint && <div className="text-muted mb-1" style={{ fontSize: 11 }}>ℹ {rangeHint}</div>}
      <div className="d-flex flex-column gap-1">
        {readings.map((row, idx) => {
          const rowVal = row.value ?? '';
          const n = parseFloat(rowVal);
          const outOfRange = rowVal !== '' && !isNaN(n) && (
            (effectiveMin !== undefined && v.deviationIfBelow && n < effectiveMin) ||
            (effectiveMax !== undefined && v.deviationIfAbove && n > effectiveMax)
          );
          const auditLabel = row.at
            ? `${row.byName || 'Unknown'} · ${new Date(row.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
            : null;
          return (
            <div key={idx} className="mb-1">
              <div className="d-flex align-items-center gap-1">
                <span className="text-muted" style={{ fontSize: 11, minWidth: 20 }}>#{idx + 1}</span>
                <div className="input-group input-group-sm flex-grow-1">
                  <input
                    type="number"
                    className={`form-control ${outOfRange ? 'border-danger' : ''}`}
                    value={rowVal}
                    onChange={e => updateRow(idx, e.target.value)}
                    placeholder={effectiveMin !== undefined && effectiveMax !== undefined ? `${effectiveMin} – ${effectiveMax}` : ''}
                    disabled={disabled}
                  />
                  {field.unit && <span className="input-group-text">{field.unit}</span>}
                </div>
                {outOfRange && <span className="text-danger" style={{ fontSize: 11 }}>⚠</span>}
                {readings.length > minCount && !disabled && (
                  <button type="button" className="btn btn-outline-secondary btn-sm px-1" onClick={() => removeRow(idx)}
                    style={{ lineHeight: 1 }} title="Remove">−</button>
                )}
              </div>
              {auditLabel && (
                <div className="text-muted ms-4" style={{ fontSize: 10 }}>{auditLabel}</div>
              )}
            </div>
          );
        })}
      </div>
      {!disabled && (
        <button type="button" className="btn btn-outline-secondary btn-sm mt-1" onClick={addRow} style={{ fontSize: 12 }}>
          + Add Reading
        </button>
      )}
      {field.helpText && <div className="text-muted mt-1" style={{ fontSize: 11 }}>{field.helpText}</div>}
    </div>
  );
}

function RadioField({ field, value, onChange, disabled }) {
  return (
    <div className="d-flex flex-column gap-2">
      {(field.options || []).filter(Boolean).map(opt => (
        <div key={opt} className="form-check">
          <input
            className="form-check-input"
            type="radio"
            id={`radio-${field.id}-${opt}`}
            name={`radio-${field.id}`}
            checked={value === opt}
            onChange={() => onChange(opt)}
            disabled={disabled}
          />
          <label className="form-check-label" htmlFor={`radio-${field.id}-${opt}`}>{opt}</label>
        </div>
      ))}
    </div>
  );
}

function PassFailField({ value, onChange, disabled }) {
  return (
    <div className="d-flex gap-2">
      <button type="button" className={`btn flex-fill ${value === 'pass' ? 'btn-success' : 'btn-outline-success'}`} style={{ minHeight: 48 }}
        onClick={() => onChange('pass')} disabled={disabled}>✓ Pass</button>
      <button type="button" className={`btn flex-fill ${value === 'fail' ? 'btn-danger' : 'btn-outline-danger'}`} style={{ minHeight: 48 }}
        onClick={() => onChange('fail')} disabled={disabled}>✗ Fail</button>
    </div>
  );
}

function YesNoField({ value, onChange, disabled }) {
  return (
    <div className="d-flex gap-2">
      <button type="button" className={`btn flex-fill ${value === 'yes' ? 'btn-primary' : 'btn-outline-primary'}`} style={{ minHeight: 48 }}
        onClick={() => onChange('yes')} disabled={disabled}>Yes</button>
      <button type="button" className={`btn flex-fill ${value === 'no' ? 'btn-danger' : 'btn-outline-danger'}`} style={{ minHeight: 48 }}
        onClick={() => onChange('no')} disabled={disabled}>No</button>
    </div>
  );
}

function SelectField({ field, value, onChange, disabled }) {
  return (
    <select className="form-select" value={value || ''} onChange={e => onChange(e.target.value)} disabled={disabled}>
      <option value="">Select…</option>
      {(field.options || []).map(opt => (
        <option key={opt} value={opt}>{opt}</option>
      ))}
    </select>
  );
}

function MultiCheckField({ field, value, onChange, disabled }) {
  const selected = value ? value.split(',').filter(Boolean) : [];
  const toggle = (opt) => {
    const next = selected.includes(opt) ? selected.filter(x => x !== opt) : [...selected, opt];
    onChange(next.join(','));
  };
  return (
    <div className="d-flex flex-column gap-1">
      {(field.options || []).map(opt => (
        <div key={opt} className="form-check">
          <input className="form-check-input" type="checkbox" id={`mc-${field.id}-${opt}`}
            checked={selected.includes(opt)} onChange={() => toggle(opt)} disabled={disabled} />
          <label className="form-check-label" htmlFor={`mc-${field.id}-${opt}`}>{opt}</label>
        </div>
      ))}
    </div>
  );
}

// A named, growable list of rows (e.g. a monthly utensil/tool count) — each row's count is
// compared against the same-named row from the most recent prior submission (seeded once at
// page load into `value` by SqfFillPage.initPage, from the backend's previousItemLists). Stores
// rows as a JSON array in valueText, same convention as MultiReadingNumberField above.
function ItemCountListField({ field, value, onChange, disabled }) {
  const v = field.validation || {};
  const mode = v.deviationMode || 'any_change';
  const threshold = v.deviationThreshold || 0;

  const rows = (() => {
    try {
      const parsed = JSON.parse(value || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch { return []; }
  })();

  const serialize = (arr) => onChange(JSON.stringify(arr));
  const updateRow = (idx, key, val) => serialize(rows.map((r, i) => (i === idx ? { ...r, [key]: val } : r)));
  const addRow = () => serialize([...rows, { itemName: '', count: '', previousCount: null, deviationNote: '' }]);
  const removeRow = (idx) => serialize(rows.filter((_, i) => i !== idx));

  return (
    <div>
      <div className="d-flex flex-column gap-2">
        {rows.map((row, idx) => {
          const isDev = computeIsDeviation(mode, threshold, row.previousCount, row.count);
          return (
            <div key={idx} className="border rounded p-2">
              <div className="d-flex gap-1 align-items-center">
                <input className="form-control form-control-sm" placeholder="Item name" value={row.itemName || ''}
                  onChange={e => updateRow(idx, 'itemName', e.target.value)} disabled={disabled} style={{ flex: 2 }} />
                <input type="number" className={`form-control form-control-sm ${isDev ? 'border-warning' : ''}`} placeholder="Count"
                  value={row.count ?? ''} onChange={e => updateRow(idx, 'count', e.target.value)} disabled={disabled} style={{ flex: '0 0 80px' }} />
                <span className="text-muted text-nowrap" style={{ fontSize: 11, flex: '0 0 70px' }}>
                  Last: {row.previousCount ?? '—'}
                </span>
                {!disabled && (
                  <button type="button" className="btn btn-outline-danger btn-sm px-2" onClick={() => removeRow(idx)} style={{ lineHeight: 1 }}>−</button>
                )}
              </div>
              {isDev && (
                <div className="mt-1">
                  <div className="text-warning" style={{ fontSize: 11 }}>
                    ⚠ Changed from last time ({row.previousCount} → {row.count}) — a note is required
                  </div>
                  <textarea className={`form-control form-control-sm mt-1 ${!(row.deviationNote || '').trim() ? 'border-danger' : ''}`} rows={2}
                    placeholder="Why did this count change?" value={row.deviationNote || ''}
                    onChange={e => updateRow(idx, 'deviationNote', e.target.value)} disabled={disabled} />
                </div>
              )}
            </div>
          );
        })}
      </div>
      {!disabled && (
        <button type="button" className="btn btn-outline-secondary btn-sm mt-2" onClick={addRow}>+ Add Item</button>
      )}
      {field.helpText && <div className="text-muted mt-1" style={{ fontSize: 11 }}>{field.helpText}</div>}
    </div>
  );
}

// Live, client-side mirror of sqfValidation.js's weight-tolerance math — for instant UX feedback
// only; the server always recomputes authoritatively from the real target weight + company
// threshold at submit time (same rounding: 1 decimal place).
function computeWeightDiff(actualValue, weightSpec) {
  const target = weightSpec?.targetWeightValue;
  const threshold = weightSpec?.deviationThresholdPercent ?? 5;
  const actual = Number(actualValue);
  if (!Number.isFinite(actual) || target == null || !Number.isFinite(Number(target)) || Number(target) === 0) {
    return { diffPercent: null, withinTolerance: null };
  }
  const diffPercent = Math.round((Math.abs(actual - Number(target)) / Number(target)) * 1000) / 10;
  return { diffPercent, withinTolerance: diffPercent <= threshold };
}

// AI-assisted Visual Inspection — QA photographs the product (ideally on a scale), the AI judges
// it against the product's spec + reference photo (set on the Product page) for each configured
// criterion, and QA comments/overrides each finding before the log can be submitted. The photo
// upload + AI analysis are both pure propose steps — nothing is persisted until this field's own
// JSON value is saved via the normal autosave/submit flow, same as every other field type.
function VisualInspectionField({ field, value, onChange, disabled, productId }) {
  const { lang } = useLanguage();
  const criteriaConfig = field.validation?.criteria || [];
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState('');
  const [localPreviewUrl, setLocalPreviewUrl] = useState(null);
  const fileRef = useRef();

  const data = (() => {
    try {
      const parsed = JSON.parse(value || 'null');
      return parsed && typeof parsed === 'object' ? parsed : { photoKey: null, criteria: [] };
    } catch { return { photoKey: null, criteria: [] }; }
  })();

  const setData = (next) => onChange(JSON.stringify(next));

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !productId) return;
    setError('');
    setUploading(true);
    setLocalPreviewUrl(URL.createObjectURL(file));
    try {
      const uploadFile = await compressImage(file).catch(() => file);
      const { uploadUrl, key } = await sqfService.getVisualInspectionUploadUrl(uploadFile.type);
      await axios.put(uploadUrl, uploadFile, { headers: { 'Content-Type': uploadFile.type } });
      setData({ ...data, photoKey: key, criteria: [] });
      setUploading(false);
      await handleAnalyze(key);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to upload photo.');
      setUploading(false);
    }
  };

  const handleAnalyze = async (photoKeyOverride) => {
    const photoKey = photoKeyOverride || data.photoKey;
    if (!photoKey || !productId || criteriaConfig.length === 0) return;
    setAnalyzing(true);
    setError('');
    try {
      const result = await sqfService.analyzeVisualInspection(photoKey, productId, criteriaConfig);
      const weightSpec = {
        targetWeightValue: result.targetWeightValue,
        targetWeightUnit: result.targetWeightUnit,
        deviationThresholdPercent: result.deviationThresholdPercent,
      };
      setData({
        photoKey,
        referenceImageUrl: result.referenceImageUrl || null,
        weightSpec,
        criteria: result.criteria.map(c => {
          const actualValue = c.key === 'weight' && c.detectedValue != null ? String(c.detectedValue) : '';
          const { diffPercent, withinTolerance } = c.key === 'weight' ? computeWeightDiff(actualValue, weightSpec) : {};
          return { ...c, qaComment: '', qaOverride: null, actualValue, diffPercent, withinTolerance };
        }),
      });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to analyze photo.');
    } finally {
      setAnalyzing(false);
    }
  };

  const updateCriterion = (idx, key, val) => {
    const nextCriteria = data.criteria.map((c, i) => {
      if (i !== idx) return c;
      const updated = { ...c, [key]: val };
      if (c.key === 'weight' && key === 'actualValue') {
        const { diffPercent, withinTolerance } = computeWeightDiff(val, data.weightSpec);
        updated.diffPercent = diffPercent;
        updated.withinTolerance = withinTolerance;
      }
      return updated;
    });
    setData({ ...data, criteria: nextCriteria });
  };

  if (!productId) {
    return (
      <div className="alert alert-warning py-2" style={{ fontSize: 12 }}>
        {t(
          'This checklist instance has no linked product/batch — Visual Inspection needs one to know which product spec to check against. Start this inspection from the Production Log, a CCP Verify page, or the SQF Hub launcher instead.',
          'Esta instancia de lista de verificación no tiene producto/lote vinculado — Inspección Visual necesita uno para saber contra qué especificación verificar. Inicie esta inspección desde el Registro de Producción, una página de Verificación CCP, o el iniciador del SQF Hub.',
          lang
        )}
      </div>
    );
  }

  return (
    <div>
      {error && <div className="alert alert-danger py-2" style={{ fontSize: 12 }}>{error}</div>}

      {!data.photoKey ? (
        <button type="button" className="btn btn-outline-primary w-100" style={{ padding: 14 }}
          onClick={() => fileRef.current?.click()} disabled={disabled || uploading}>
          {uploading ? <><span className="spinner-border spinner-border-sm me-2" />{t('Uploading…', 'Subiendo…', lang)}</> : `📷 ${t('Take / Select Photo', 'Tomar / Seleccionar Foto', lang)}`}
        </button>
      ) : (
        <div>
          {localPreviewUrl
            ? <img src={localPreviewUrl} alt="inspection" style={{ maxWidth: '100%', maxHeight: 220, borderRadius: 6, border: '1px solid #dee2e6' }} className="mb-2 d-block" />
            : <div className="text-muted mb-2" style={{ fontSize: 12 }}>📷 {t('Photo on file', 'Foto en archivo', lang)}</div>}
          {!disabled && (
            <button type="button" className="btn btn-outline-secondary btn-sm mb-2" onClick={() => fileRef.current?.click()} disabled={uploading || analyzing}>
              {t('↻ Retake Photo', '↻ Volver a Tomar Foto', lang)}
            </button>
          )}
        </div>
      )}
      <input ref={fileRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={handleFileChange} disabled={disabled} />

      {analyzing && (
        <div className="text-muted mt-2" style={{ fontSize: 12 }}>
          <span className="spinner-border spinner-border-sm me-2" />{t('Analyzing photo with AI…', 'Analizando foto con IA…', lang)}
        </div>
      )}

      {!analyzing && data.photoKey && data.criteria.length === 0 && !disabled && (
        <button type="button" className="btn btn-outline-primary btn-sm mt-2" onClick={() => handleAnalyze()}>
          🤖 {t('Analyze Photo', 'Analizar Foto', lang)}
        </button>
      )}

      {data.criteria.length > 0 && (
        <div className="mt-2 d-flex flex-column gap-2">
          {data.criteria.map((c, idx) => {
            const isWeight = c.key === 'weight';
            const weightSpec = data.weightSpec;
            const weightOutOfTolerance = isWeight && c.withinTolerance === false;
            const needsComment = (c.aiStatus === 'flag' || weightOutOfTolerance) && !(c.qaComment || '').trim();
            const cardFlagged = c.aiStatus === 'flag' || weightOutOfTolerance;
            return (
              <div key={c.key} className={`border rounded p-2 ${cardFlagged ? 'border-danger' : ''}`}>
                <div className="d-flex justify-content-between align-items-start">
                  <div className="fw-semibold" style={{ fontSize: 13 }}>{c.label}</div>
                  <span className={`badge bg-${STATUS_BADGE[c.aiStatus] || 'secondary'}`} style={{ fontSize: 10 }}>
                    {c.aiStatus} {c.aiConfidence ? `(${c.aiConfidence})` : ''}
                  </span>
                </div>
                <div className="text-muted mt-1" style={{ fontSize: 12 }}>{c.aiFinding}</div>

                {isWeight && (
                  <div className="mt-2 p-2 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                    <div className="d-flex flex-wrap gap-2 align-items-end">
                      <div>
                        <label className="form-label mb-0" style={{ fontSize: 11 }}>{t('Target weight', 'Peso objetivo', lang)}</label>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>
                          {weightSpec?.targetWeightValue != null ? `${weightSpec.targetWeightValue} ${weightSpec.targetWeightUnit || ''}` : t('Not set on product', 'No configurado en el producto', lang)}
                        </div>
                      </div>
                      <div>
                        <label className="form-label mb-0" style={{ fontSize: 11 }}>{t('Actual weight', 'Peso real', lang)}</label>
                        <div className="input-group input-group-sm" style={{ width: 150 }}>
                          <input type="number" inputMode="decimal" step="0.01" className="form-control"
                            value={c.actualValue ?? ''} onChange={e => updateCriterion(idx, 'actualValue', e.target.value)} disabled={disabled} />
                          <span className="input-group-text">{weightSpec?.targetWeightUnit || ''}</span>
                        </div>
                      </div>
                      {c.diffPercent != null && (
                        <span className={`badge bg-${c.withinTolerance ? 'success' : 'danger'}`} style={{ fontSize: 11 }}>
                          {c.withinTolerance ? '✓' : '✗'} {c.diffPercent}% {t('diff', 'dif.', lang)} ({t('threshold', 'umbral', lang)} {weightSpec?.deviationThresholdPercent}%)
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {c.aiStatus === 'flag' && (
                  <select className="form-select form-select-sm mt-2 mb-1" style={{ maxWidth: 260 }}
                    value={c.qaOverride || ''} onChange={e => updateCriterion(idx, 'qaOverride', e.target.value || null)} disabled={disabled}>
                    <option value="">{t('— No override —', '— Sin anulación —', lang)}</option>
                    <option value="agree">{t('Agree — this is a real issue', 'De acuerdo — es un problema real', lang)}</option>
                    <option value="disagree">{t("Disagree — AI is wrong, this is fine", 'En desacuerdo — la IA se equivoca, está bien', lang)}</option>
                  </select>
                )}
                <textarea className={`form-control form-control-sm mt-1 ${needsComment ? 'border-danger' : ''}`} rows={cardFlagged ? 2 : 1}
                  placeholder={cardFlagged
                    ? t('Justification (required)', 'Justificación (requerida)', lang)
                    : t('QA comment (optional)', 'Comentario de QA (opcional)', lang)}
                  value={c.qaComment || ''} onChange={e => updateCriterion(idx, 'qaComment', e.target.value)} disabled={disabled} />
                {needsComment && <div className="text-danger" style={{ fontSize: 11 }}>{t('A comment is required for this flagged criterion.', 'Se requiere un comentario para este criterio marcado.', lang)}</div>}
              </div>
            );
          })}
        </div>
      )}

      {field.helpText && <div className="text-muted mt-1" style={{ fontSize: 11 }}>{field.helpText}</div>}
    </div>
  );
}

function FieldInput({ field, value, onChange, disabled, responses, allFields, productId }) {
  switch (field.type) {
    case 'number':
      return field.multipleReadings
        ? <MultiReadingNumberField field={field} value={value} onChange={onChange} disabled={disabled} responses={responses} allFields={allFields} />
        : <NumberField field={field} value={value} onChange={onChange} disabled={disabled} responses={responses} allFields={allFields} />;
    case 'pass_fail':  return <PassFailField value={value} onChange={onChange} disabled={disabled} />;
    case 'yes_no':     return <YesNoField value={value} onChange={onChange} disabled={disabled} />;
    case 'select':     return <SelectField field={field} value={value} onChange={onChange} disabled={disabled} />;
    case 'radio':      return <RadioField field={field} value={value} onChange={onChange} disabled={disabled} />;
    case 'multi_check': return <MultiCheckField field={field} value={value} onChange={onChange} disabled={disabled} />;
    case 'item_count_list': return <ItemCountListField field={field} value={value} onChange={onChange} disabled={disabled} />;
    case 'visual_inspection': return <VisualInspectionField field={field} value={value} onChange={onChange} disabled={disabled} productId={productId} />;
    case 'time':
      return <input type="time" className="form-control" value={value || ''} onChange={e => onChange(e.target.value)} disabled={disabled} />;
    case 'text':
    default:
      return (
        <textarea className="form-control" rows={2} value={value || ''} onChange={e => onChange(e.target.value)}
          placeholder={field.helpText || ''} disabled={disabled} />
      );
  }
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function SqfFillPage() {
  const { instanceId } = useParams();

  const [instance, setInstance] = useState(null);
  const [log, setLog] = useState(null);
  const [schema, setSchema] = useState(null);
  const [responses, setResponses] = useState({}); // fieldId -> valueText
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [lastSaved, setLastSaved] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [missingFields, setMissingFields] = useState([]);
  const [lockConflict, setLockConflict] = useState(null); // { lockedByUserName, lockedAt }

  const autoSaveTimer = useRef(null);

  const initPage = useCallback(async () => {
    try {
      const inst = await sqfService.getInstance(instanceId);
      setInstance(inst);

      let currentLog = inst.Log;
      if (!currentLog) {
        currentLog = await sqfService.startLog(instanceId);
      } else {
        // Log exists — still acquire the lock to signal we're actively filling
        try {
          const lockResult = await sqfService.lockInstance(instanceId);
          if (lockResult.conflict) {
            setLockConflict({ lockedByUserName: lockResult.lockedByUserName, lockedAt: lockResult.lockedAt });
          }
        } catch { /* non-fatal */ }
      }
      setLog(currentLog);

      // Load schema from version
      const { instance: fullInst, previousItemLists } = await sqfService.getInstanceFull(instanceId);
      const schemaJson = fullInst.Log?.TemplateVersion?.schemaJson;
      if (schemaJson) setSchema(schemaJson);

      // Seed responses from existing saved responses
      const map = {};
      if (fullInst.Log?.Responses?.length > 0) {
        fullInst.Log.Responses.forEach(r => { map[r.fieldId] = r.valueText || ''; });
      }

      // For any item_count_list field with no saved response yet, pre-populate its row names
      // (blank counts) from the most recent prior submission of this same template.
      if (schemaJson) {
        for (const section of (schemaJson.sections || [])) {
          for (const field of (section.fields || [])) {
            if (field.type === 'item_count_list' && !map[field.id]) {
              const prevRows = previousItemLists?.[field.id] || [];
              if (prevRows.length > 0) {
                map[field.id] = JSON.stringify(prevRows.map(pr => ({
                  itemName: pr.itemName, count: '', previousCount: pr.count, deviationNote: '',
                })));
              }
            }
          }
        }
      }
      if (Object.keys(map).length > 0) setResponses(map);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load checklist.');
    } finally {
      setLoading(false);
    }
  }, [instanceId]);

  useEffect(() => { initPage(); }, [initPage]);

  // Release lock when navigating away
  useEffect(() => {
    return () => {
      sqfService.releaseLock(instanceId).catch(() => {});
    };
  }, [instanceId]);

  const isLocked = log?.qaStatus === 'qa_approved';
  const { lang } = useLanguage();

  // Auto-save every 30 seconds — only when not locked
  useEffect(() => {
    if (!log || isLocked) return;
    autoSaveTimer.current = setInterval(async () => {
      if (Object.keys(responses).length === 0) return;
      try {
        const payload = buildResponsePayload(schema, responses);
        await sqfService.saveResponses(log.id, payload);
        setLastSaved(new Date());
      } catch { /* silent */ }
    }, 30000);
    return () => clearInterval(autoSaveTimer.current);
  }, [log, isLocked, responses, schema]);

  function buildResponsePayload(schemaJson, respMap) {
    if (!schemaJson) return [];
    const fields = (schemaJson.sections || []).flatMap(s => s.fields || []);
    return Object.entries(respMap).map(([fieldId, valueText]) => {
      const field = fields.find(f => f.id === fieldId);
      return {
        fieldId,
        fieldLabel: field?.label || fieldId,
        fieldType: field?.type || 'text',
        valueText: valueText ?? null,
      };
    });
  }

  const handleChange = (fieldId, value) => {
    setResponses(prev => ({ ...prev, [fieldId]: value }));
  };

  const handleManualSave = async () => {
    if (!log) return;
    setSaving(true);
    try {
      await sqfService.saveResponses(log.id, buildResponsePayload(schema, responses));
      setLastSaved(new Date());
    } catch {
      setError('Failed to save responses.');
    } finally {
      setSaving(false);
    }
  };

  // Returns array of { label, labelEs } for required fields that are empty or under-filled
  function getMissingRequired(schemaJson, respMap) {
    if (!schemaJson) return [];
    const missing = [];
    for (const section of (schemaJson.sections || [])) {
      for (const field of (section.fields || [])) {
        const value = respMap[field.id];

        // Checked regardless of field.required — a deviating row always needs its own note
        // before submit, independent of whether the field itself was marked required.
        if (field.type === 'item_count_list') {
          let rows = [];
          try { rows = JSON.parse(value || '[]'); } catch { rows = []; }
          if (!Array.isArray(rows)) rows = [];
          const namedRows = rows.filter(r => r && String(r.itemName || '').trim() !== '');
          if (field.required && namedRows.length === 0) {
            missing.push({ label: field.label, labelEs: field.labelEs });
          }
          const v = field.validation || {};
          const mode = v.deviationMode || 'any_change';
          const threshold = v.deviationThreshold || 0;
          for (const r of namedRows) {
            if (computeIsDeviation(mode, threshold, r.previousCount, r.count) && !(r.deviationNote || '').trim()) {
              missing.push({
                label: `${field.label} — note required for "${r.itemName}"`,
                labelEs: `${field.labelEs || field.label} — nota requerida para "${r.itemName}"`,
              });
            }
          }
          continue;
        }

        // Checked regardless of field.required — a flagged criterion always needs a QA comment
        // before submit, and a required field needs at least a photo analyzed.
        if (field.type === 'visual_inspection') {
          let vi = null;
          try { vi = JSON.parse(value || 'null'); } catch { vi = null; }
          const criteria = Array.isArray(vi?.criteria) ? vi.criteria : [];
          if (field.required && (!vi?.photoKey || criteria.length === 0)) {
            missing.push({ label: field.label, labelEs: field.labelEs });
          }
          for (const c of criteria) {
            const weightOutOfTolerance = c.key === 'weight' && c.withinTolerance === false;
            if ((c.aiStatus === 'flag' || weightOutOfTolerance) && !(c.qaComment || '').trim()) {
              missing.push({
                label: `${field.label} — comment required for "${c.label}"`,
                labelEs: `${field.labelEs || field.label} — comentario requerido para "${c.label}"`,
              });
            }
          }
          continue;
        }

        if (!field.required) continue;
        if (field.multipleReadings) {
          const minCount = field.minReadings ?? 1;
          let filled = 0;
          try {
            const parsed = JSON.parse(value || '[]');
            if (Array.isArray(parsed)) {
              filled = parsed
                .map(r => (typeof r === 'object' && r !== null ? r.value : r))
                .filter(v => v !== '' && v !== null && v !== undefined).length;
            }
          } catch { /* ignore */ }
          if (filled < minCount) missing.push({ label: field.label, labelEs: field.labelEs });
        } else {
          if (!value || value.toString().trim() === '') {
            missing.push({ label: field.label, labelEs: field.labelEs });
          }
        }
      }
    }
    return missing;
  }

  const handleRequestSubmit = () => {
    const missing = getMissingRequired(schema, responses);
    if (missing.length > 0) {
      setMissingFields(missing);
      window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
      return;
    }
    setMissingFields([]);
    setShowConfirm(true);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = buildResponsePayload(schema, responses);
      const result = await sqfService.submitLog(log.id, { responses: payload });
      sqfService.releaseLock(instanceId).catch(() => {});
      setSubmitResult(result);
      setShowConfirm(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to submit.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="container mt-4 text-center py-5 text-muted">
      <div className="spinner-border spinner-border-sm me-2" />{t('Loading…', 'Cargando…', lang)}
    </div>
  );

  if (submitResult) {
    const devCount = submitResult.deviations.length;
    return (
      <div className="container mt-4 pb-5" style={{ maxWidth: 480 }}>
        <div className={`alert ${submitResult.hasDeviation ? 'alert-warning' : 'alert-success'} text-center`}>
          <div style={{ fontSize: 40 }}>{submitResult.hasDeviation ? '⚠️' : '✅'}</div>
          <h5 className="mt-2">
            {submitResult.hasDeviation
              ? t('Submitted with Deviations', 'Enviado con Desviaciones', lang)
              : t('Checklist Submitted', 'Lista de Verificación Enviada', lang)}
          </h5>
          {submitResult.hasDeviation && (
            <p className="mb-2" style={{ fontSize: 13 }}>
              {t(
                `${devCount} deviation${devCount !== 1 ? 's' : ''} flagged and forwarded to QA.`,
                `${devCount} desviación${devCount !== 1 ? 'es' : ''} registrada${devCount !== 1 ? 's' : ''} y enviada${devCount !== 1 ? 's' : ''} a QA.`,
                lang
              )}
            </p>
          )}
        </div>
        {submitResult.hasDeviation && (
          <div className="mb-3">
            <strong style={{ fontSize: 13 }}>{t('Deviations:', 'Desviaciones:', lang)}</strong>
            <ul className="mt-1" style={{ fontSize: 13 }}>
              {submitResult.deviations.map(d => (
                <li key={d.fieldId}>
                  <strong>{d.fieldLabel}</strong>: {formatValue(d.valueText)}
                  {d.deviationReason && (
                    <span className="text-muted ms-1" style={{ fontSize: 11 }}>({d.deviationReason})</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="d-flex gap-2">
          <Link to="/sqf" className="btn btn-outline-secondary flex-fill">
            {t('Dashboard', 'Panel', lang)}
          </Link>
          {submitResult.log && (
            <Link to={`/sqf/logs/${submitResult.log.id}`} className="btn btn-primary flex-fill">
              {t('View Log', 'Ver Registro', lang)}
            </Link>
          )}
        </div>
      </div>
    );
  }

  const sections = schema?.sections || [];
  const allFields = sections.flatMap(s => s.fields || []);
  const tmpl = instance?.Template;
  const templateName = t(tmpl?.name, tmpl?.nameEs, lang) || 'Checklist';

  return (
    <div className="pb-5" style={{ maxWidth: 540, margin: '0 auto' }}>
      {/* Sticky header */}
      <div className="bg-white border-bottom px-3 py-2 d-flex justify-content-between align-items-center" style={{ position: 'sticky', top: 56, zIndex: 999 }}>
        <div>
          <Link to="/sqf/checklists" className="text-muted" style={{ fontSize: 12 }}>
            ← {t('Checklists', 'Listas', lang)}
          </Link>
          <div className="fw-semibold" style={{ fontSize: 15 }}>{templateName}</div>
          {instance?.dueAt && (
            <div className="text-muted" style={{ fontSize: 11 }}>
              {t('Due', 'Vence a las', lang)} {new Date(instance.dueAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </div>
          )}
        </div>
        <div className="d-flex align-items-center gap-2">
          {isLocked
            ? <span className="badge bg-success">{t('QA Approved', 'Aprobado por QA', lang)}</span>
            : <>
                {lastSaved && (
                  <span className="text-success" style={{ fontSize: 11 }}>
                    {t('Saved', 'Guardado', lang)} {lastSaved.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                )}
                <button className="btn btn-outline-secondary btn-sm" onClick={handleManualSave} disabled={saving}>
                  {saving ? '…' : t('Save', 'Guardar', lang)}
                </button>
              </>
          }
        </div>
      </div>

      <div className="px-3 pt-3">
        {lockConflict && (
          <div className="alert alert-warning py-2 mb-3 d-flex align-items-center gap-2" style={{ fontSize: 13 }}>
            <span>⚠️</span>
            <span className="flex-fill">
              {t(
                <><strong>{lockConflict.lockedByUserName}</strong> is already filling this checklist. Check with them before making changes.</>,
                <><strong>{lockConflict.lockedByUserName}</strong> ya está completando esta lista. Consulta con esa persona antes de hacer cambios.</>,
                lang
              )}
            </span>
            <button className="btn-close btn-sm" onClick={() => setLockConflict(null)} aria-label="Close" />
          </div>
        )}
        {isLocked && (
          <div className="alert alert-success py-2 mb-3 d-flex align-items-center gap-2" style={{ fontSize: 13 }}>
            <span>🔒</span>
            <span>
              {t(
                <>This checklist has been <strong>QA approved</strong> and is locked for editing.</>,
                <>Esta lista ha sido <strong>aprobada por QA</strong> y no se puede editar.</>,
                lang
              )}
            </span>
          </div>
        )}
        {error && <div className="alert alert-danger py-2 mb-3">{error}</div>}

        {sections.length === 0 ? (
          <div className="text-center text-muted py-5">
            {t('No fields in this checklist template.', 'Esta plantilla no tiene campos.', lang)}
          </div>
        ) : sections.map(section => (
          <div key={section.id} className="card mb-3 shadow-sm">
            {section.title && (
              <div className="card-header py-2 fw-semibold" style={{ fontSize: 14 }}>
                {t(section.title, section.titleEs, lang)}
              </div>
            )}
            <div className="card-body py-2 px-3">
              {(section.fields || []).map(field => (
                <div key={field.id} className="mb-3">
                  <label className="form-label mb-1" style={{ fontSize: 13, fontWeight: 600 }}>
                    {t(field.label, field.labelEs, lang)}
                    {field.required && <span className="text-danger ms-1">*</span>}
                  </label>
                  {field.helpText && (
                    <div className="text-muted mb-1" style={{ fontSize: 11 }}>
                      {t(field.helpText, field.helpTextEs, lang)}
                    </div>
                  )}
                  <FieldInput field={field} value={responses[field.id] || ''} onChange={v => { handleChange(field.id, v); if (missingFields.length) setMissingFields([]); }} disabled={isLocked} responses={responses} allFields={allFields} productId={instance?.productId} />
                </div>
              ))}
            </div>
          </div>
        ))}

        {!isLocked && (
          <>
            {missingFields.length > 0 && (
              <div className="alert alert-danger py-2 mb-2" style={{ fontSize: 13 }}>
                <strong>{t('Please fill in all required fields before submitting:', 'Complete todos los campos obligatorios antes de enviar:', lang)}</strong>
                <ul className="mb-0 mt-1">
                  {missingFields.map((f, i) => (
                    <li key={i}>{t(f.label, f.labelEs, lang)}</li>
                  ))}
                </ul>
              </div>
            )}
            <button
              className="btn btn-success w-100 py-2 mt-2" style={{ fontSize: 16 }}
              onClick={handleRequestSubmit} disabled={submitting}>
              {t('Submit Checklist', 'Enviar Lista de Verificación', lang)}
            </button>
          </>
        )}
      </div>

      {/* Submit confirmation modal */}
      {showConfirm && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.4)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {t('Submit Checklist?', '¿Enviar Lista de Verificación?', lang)}
                </h5>
                <button className="btn-close" onClick={() => setShowConfirm(false)} />
              </div>
              <div className="modal-body">
                <p style={{ fontSize: 14 }}>
                  {t(
                    'Once submitted, you cannot make changes. The checklist will be forwarded to QA for review.',
                    'Una vez enviada, no podrá realizar cambios. La lista será enviada a QA para revisión.',
                    lang
                  )}
                </p>
                {schema?.requiresQaReview && (
                  <div className="alert alert-info py-2" style={{ fontSize: 13 }}>
                    {t(
                      'This checklist requires QA approval after submission.',
                      'Esta lista requiere aprobación de QA tras el envío.',
                      lang
                    )}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowConfirm(false)}>
                  {t('Cancel', 'Cancelar', lang)}
                </button>
                <button className="btn btn-success" onClick={handleSubmit} disabled={submitting}>
                  {submitting
                    ? t('Submitting…', 'Enviando…', lang)
                    : t('Confirm Submit', 'Confirmar Envío', lang)}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
